import { useState, useCallback, useEffect } from "react";

export const PLAYER_COUNT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8];

export const TIME_OPTIONS = [
  { label: "Quick", value: "short" },
  { label: "Standard", value: "medium" },
  { label: "Epic", value: "long" }
];

export const ENERGY_OPTIONS = [
  { label: "Chill", value: "low" },
  { label: "Balanced", value: "medium" },
  { label: "Intense", value: "high" }
];

export const FEEL_OPTIONS = [
  { label: "Competitive", value: "competitive" },
  { label: "Aggressive", value: "aggressive" },
  { label: "Neutral", value: "neutral" },
  { label: "Social", value: "social" },
  { label: "Cooperative", value: "cooperative" }
];

// User-facing labels for summary badges (selection labels in FEEL_OPTIONS stay as-is)
export const FEEL_DISPLAY_LABELS = {
  competitive: "Competitive",
  aggressive: "Mild Competitive",
  neutral: "Neutral",
  social: "Not Fully Co-operative",
  cooperative: "Cooperative"
};

export const getFeelDisplayLabel = (value) => FEEL_DISPLAY_LABELS[value] || value;

export const FAMILIARITY_OPTIONS = [
  { label: "Familiar Games", value: "familiar" },
  { label: "New Games", value: "new" },
  { label: "No Preference", value: "neutral" }
];

const STORAGE_KEY = "jetSetGoPreferences";

const DEFAULT_PREFERENCES = {
  players: 2,
  time: "medium",
  energy: "medium",
  feel: "neutral",
  familiarity: "any",
  useCollection: false
};

const PERSISTED_FIELDS = ["players", "time", "energy", "feel"];

const loadSavedPreferences = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed === null || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const isValidValue = (key, value) => {
  if (key === "players") {
    return Number.isInteger(value) && value >= 1 && value <= 8;
  }
  if (key === "time") {
    return TIME_OPTIONS.some((opt) => opt.value === value);
  }
  if (key === "energy") {
    return ENERGY_OPTIONS.some((opt) => opt.value === value);
  }
  if (key === "feel") {
    return FEEL_OPTIONS.some((opt) => opt.value === value);
  }
  return false;
};

const buildInitialPreferences = () => {
  const saved = loadSavedPreferences() || {};
  const restored = { ...DEFAULT_PREFERENCES };

  PERSISTED_FIELDS.forEach((key) => {
    const value = saved[key];
    if (isValidValue(key, value)) {
      restored[key] = value;
    }
  });

  return restored;
};

export function useSimplifyPreferences() {
  const [preferences, setPreferences] = useState(buildInitialPreferences);

  useEffect(() => {
    try {
      const toSave = {};
      PERSISTED_FIELDS.forEach((key) => {
        toSave[key] = preferences[key];
      });
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
    } catch {
      // Ignore localStorage errors (e.g. private mode, quota exceeded)
    }
  }, [preferences]);

  const setPreference = useCallback((key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  }, []);

  const setPlayers = useCallback((players) => {
    setPreferences(prev => ({ ...prev, players }));
  }, []);

  const setTime = useCallback((time) => {
    setPreferences(prev => ({ ...prev, time }));
  }, []);

  const setEnergy = useCallback((energy) => {
    setPreferences(prev => ({ ...prev, energy }));
  }, []);

  const setFeel = useCallback((feel) => {
    setPreferences(prev => ({ ...prev, feel }));
  }, []);

  const setFamiliarity = useCallback((familiarity) => {
    setPreferences(prev => ({ ...prev, familiarity }));
  }, []);

  const setUseCollection = useCallback((useCollection) => {
    setPreferences(prev => ({ ...prev, useCollection }));
  }, []);

  const resetPreferences = useCallback(() => {
    setPreferences({
      players: null,
      time: null,
      energy: null,
      feel: null,
      familiarity: "neutral",
      useCollection: false
    });
  }, []);

  return {
    preferences,
    setPreferences,
    setPreference,
    setPlayers,
    setTime,
    setEnergy,
    setFeel,
    setFamiliarity,
    setUseCollection,
    resetPreferences
  };
}
